/**
 * Ad Service - Handles ads for Web (AdSense) and Native (AdMob)
 *
 * Web: Uses Google AdSense display ads
 * iOS/Android: Uses Google AdMob rewarded video ads via @capacitor-community/admob
 *
 * NOTE: The @capacitor-community/admob package must be installed:
 *   npm install @capacitor-community/admob
 *   npx cap sync
 */

import { isPlatform } from "@ionic/react";

// Conditional import for AdMob (only available in native builds)
// We use a try-catch wrapper to handle cases where the module doesn't exist (web)
let AdMobModule: any = null;
let RewardAdPluginEvents: any = null;

// Try to import AdMob statically - this will only work in native builds
// In web, this will be null and we handle it gracefully
try {
  // @ts-ignore - Module may not exist in web builds
  const admobImport = require("@capacitor-community/admob");
  AdMobModule = admobImport;
  RewardAdPluginEvents = admobImport.RewardAdPluginEvents || null;
} catch (e) {
  // Module doesn't exist (web build) - this is expected
  console.log("[AdService] AdMob module not available (web build)");
}

// =============================================================================
// Platform Detection
// =============================================================================

export const isNativePlatform = () => isPlatform("hybrid");
export const isWebPlatform = () => !isPlatform("hybrid");

// =============================================================================
// AdMob Configuration (iOS/Android only)
// =============================================================================

// Test IDs (USE THESE FOR DEVELOPMENT - NEVER USE PRODUCTION IDS IN DEV)
export const ADMOB_CONFIG = {
  // Test App IDs (from Google's official test suite)
  testAppId: {
    ios: "ca-app-pub-3940256099942544~1458002511",
    android: "ca-app-pub-3940256099942544~3347511713",
  },
  // Test Ad Unit IDs (from Google's official test suite)
  testAdUnitId: {
    rewardedVideo: {
      ios: "ca-app-pub-3940256099942544/1712485313",
      android: "ca-app-pub-3940256099942544/5224354917",
    },
    interstitial: {
      ios: "ca-app-pub-3940256099942544/4411468910",
      android: "ca-app-pub-3940256099942544/1033173712",
    },
  },
  //Production IDs
  productionAppId: {
    ios: "ca-app-pub-2386117549228433~3807187879",
    android: "ca-app-pub-2386117549228433~1424567117",
  },
  productionAdUnitId: {
    rewardedVideo: {
      ios: "ca-app-pub-2386117549228433/7943735822",
      android: "ca-app-pub-2386117549228433/6630654156",
    },
  },
};

// =============================================================================
// AdSense Configuration (Web only)
// =============================================================================

export const ADSENSE_CONFIG = {
  // TODO: Replace with your AdSense publisher ID when approved
  publisherId: "ca-pub-2386117549228433", // Your AdSense publisher ID
  // TODO: Replace with your ad slot ID when created
  adSlotId: "XXXXXXXXXX", // Your ad slot ID
  // Test mode flag (set to false in production)
  testMode: true,
};

// =============================================================================
// Ad State Types
// =============================================================================

export type AdState = "idle" | "loading" | "ready" | "showing" | "completed" | "failed" | "skipped";

export interface AdCallbacks {
  onAdLoading?: () => void;
  onAdLoaded?: () => void;
  onAdShowing?: () => void;
  onAdCompleted?: () => void;
  onAdFailed?: (error: string) => void;
  onAdSkipped?: () => void;
  onRewardEarned?: (reward: { type: string; amount: number }) => void;
}

// =============================================================================
// AdMob Service (Native Platforms)
// =============================================================================

let admobInitialized = false;
let AdMob: any = null;

/**
 * Initialize AdMob SDK (call once at app startup for native platforms)
 */
export const initializeAdMob = async (): Promise<boolean> => {
  if (!isNativePlatform()) {
    console.log("[AdService] Not a native platform, skipping AdMob initialization");
    return false;
  }

  if (admobInitialized) {
    console.log("[AdService] AdMob already initialized");
    return true;
  }

  // Check if module is available
  if (!AdMobModule || !AdMobModule.AdMob) {
    console.warn("[AdService] AdMob module not available - make sure @capacitor-community/admob is installed");
    return false;
  }

  try {
    AdMob = AdMobModule.AdMob;

    // Use production mode when NODE_ENV is production
    const isProduction = process.env.NODE_ENV === "production";
    console.log("[AdService] isProduction:", isProduction);

    await AdMob.initialize({
      requestTrackingAuthorization: true, // Required for iOS 14+ ATT
      // testingDevices: [], // Add your device IDs for testing
      // initializeForTesting: true, // Set to false for production
      initializeForTesting: !isProduction, // false in production, true in development
    });

    admobInitialized = true;
    console.log("[AdService] AdMob initialized successfully");
    return true;
  } catch (error) {
    console.error("[AdService] Failed to initialize AdMob:", error);
    return false;
  }
};

/**
 * Prepare a rewarded video ad (preload for better UX)
 */
export const prepareRewardedAd = async (): Promise<boolean> => {
  if (!isNativePlatform() || !AdMob) {
    return false;
  }

  try {
    // const { RewardAdOptions } = await import("@capacitor-community/admob");

    const isIOS = isPlatform("ios");
    const isProduction = process.env.NODE_ENV === "production";
    // Use production Ad Unit IDs in production, test IDs in development
    const adId = isProduction
      ? isIOS
        ? ADMOB_CONFIG.productionAdUnitId.rewardedVideo.ios
        : ADMOB_CONFIG.productionAdUnitId.rewardedVideo.android
      : isIOS
      ? ADMOB_CONFIG.testAdUnitId.rewardedVideo.ios
      : ADMOB_CONFIG.testAdUnitId.rewardedVideo.android;
    // const adId = isIOS ? ADMOB_CONFIG.testAdUnitId.rewardedVideo.ios : ADMOB_CONFIG.testAdUnitId.rewardedVideo.android;

    const options = {
      adId: adId,
      // isTesting: true, // true in production, true in development
      isTesting: !isProduction, // false in production, true in development
      // npa: true, // Non-personalized ads (for GDPR compliance)
    };

    await AdMob.prepareRewardVideoAd(options);
    console.log("[AdService] Rewarded ad prepared successfully");
    return true;
  } catch (error) {
    console.error("[AdService] Failed to prepare rewarded ad:", error);
    return false;
  }
};

/**
 * Show rewarded video ad with callbacks
 */
export const showRewardedAd = async (callbacks?: AdCallbacks): Promise<boolean> => {
  if (!isNativePlatform() || !AdMob) {
    console.log("[AdService] Not on native platform, cannot show AdMob ad");
    return false;
  }

  // Check if RewardAdPluginEvents is available
  if (!RewardAdPluginEvents) {
    console.error("[AdService] RewardAdPluginEvents not available");
    callbacks?.onAdFailed?.("AdMob module not properly loaded");
    return false;
  }

  try {
    // Set up event listeners
    const listeners: any[] = [];
    let rewardEarned = false; // Track if reward was earned to differentiate skip vs complete in Dismissed event

    // Helper function to clean up all listeners
    const cleanupListeners = () => {
      listeners.forEach((listener) => {
        if (listener && listener.remove) {
          listener.remove();
        }
      });
      listeners.length = 0; // Clear array
    };

    // Listener for when user earns reward (completed video)
    listeners.push(
      AdMob.addListener(RewardAdPluginEvents.Rewarded, (reward: any) => {
        console.log("[AdService] User earned reward:", reward);
        rewardEarned = true;
        callbacks?.onRewardEarned?.({ type: reward.type, amount: reward.amount });
        callbacks?.onAdCompleted?.();
      })
    );

    // Listener for ad loaded
    listeners.push(
      AdMob.addListener(RewardAdPluginEvents.Loaded, () => {
        console.log("[AdService] Rewarded ad loaded");
        callbacks?.onAdLoaded?.();
      })
    );

    // Listener for ad failed to load
    listeners.push(
      AdMob.addListener(RewardAdPluginEvents.FailedToLoad, (error: any) => {
        console.error("[AdService] Rewarded ad failed to load:", error);
        callbacks?.onAdFailed?.(error?.message || "Failed to load ad");
        cleanupListeners(); // Clean up immediately on failure
      })
    );

    // Listener for ad shown
    listeners.push(
      AdMob.addListener(RewardAdPluginEvents.Showed, () => {
        console.log("[AdService] Rewarded ad shown");
        callbacks?.onAdShowing?.();
      })
    );

    // Listener for ad dismissed (closed without completing)
    listeners.push(
      AdMob.addListener(RewardAdPluginEvents.Dismissed, () => {
        console.log("[AdService] Rewarded ad dismissed");
        // Only call onAdSkipped if reward was NOT earned
        if (!rewardEarned) {
          callbacks?.onAdSkipped?.();
        }
        // Clean up listeners after ad is dismissed
        cleanupListeners();
      })
    );

    callbacks?.onAdLoading?.();

    // Prepare and show the ad
    const isIOS = isPlatform("ios");
    const isProduction = process.env.NODE_ENV === "production";

    // Use production Ad Unit IDs in production, test IDs in development
    const adId = isProduction
      ? isIOS
        ? ADMOB_CONFIG.productionAdUnitId.rewardedVideo.ios
        : ADMOB_CONFIG.productionAdUnitId.rewardedVideo.android
      : isIOS
      ? ADMOB_CONFIG.testAdUnitId.rewardedVideo.ios
      : ADMOB_CONFIG.testAdUnitId.rewardedVideo.android;
    // const adId = isIOS ? ADMOB_CONFIG.testAdUnitId.rewardedVideo.ios : ADMOB_CONFIG.testAdUnitId.rewardedVideo.android;

    await AdMob.prepareRewardVideoAd({
      adId: adId,
      // isTesting: true, // true in production, true in development
      isTesting: !isProduction, // false in production, true in development
    });

    await AdMob.showRewardVideoAd();

    // Fallback: Clean up listeners after 60 seconds if they weren't cleaned up yet
    setTimeout(() => {
      if (listeners.length > 0) {
        console.warn("[AdService] Cleaning up listeners via fallback timeout");
        cleanupListeners();
      }
    }, 60000);

    return true;
  } catch (error) {
    console.error("[AdService] Failed to show rewarded ad:", error);
    callbacks?.onAdFailed?.((error as Error)?.message || "Failed to show ad");
    return false;
  }
};

// =============================================================================
// AdSense Service (Web Platform)
// =============================================================================

/**
 * Check if AdSense script is loaded
 */
export const isAdSenseLoaded = (): boolean => {
  return typeof (window as any).adsbygoogle !== "undefined";
};

/**
 * Load AdSense script dynamically (if not already loaded)
 * Note: For production, add the script to index.html instead
 */
export const loadAdSenseScript = async (): Promise<boolean> => {
  if (!isWebPlatform()) {
    return false;
  }

  //if the ad is already loaded, return true
  if (isAdSenseLoaded()) {
    return true;
  }

  return new Promise((resolve) => {
    //if not loaded, load the script
    const script = document.createElement("script");
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CONFIG.publisherId}`;
    script.async = true;
    script.crossOrigin = "anonymous";

    script.onload = () => {
      console.log("[AdService] AdSense script loaded");
      resolve(true);
    };

    script.onerror = () => {
      console.error("[AdService] Failed to load AdSense script");
      resolve(false);
    };

    document.head.appendChild(script);
  });
};

/**
 * Trigger AdSense ad refresh, this means that the ad will be refreshed and a new ad will be shown
 */
export const refreshAdSenseAd = (): void => {
  if (!isWebPlatform() || !isAdSenseLoaded()) {
    return;
  }

  try {
    ((window as any).adsbygoogle = (window as any).adsbygoogle || []).push({});
  } catch (error) {
    console.error("[AdService] Failed to refresh AdSense ad:", error);
  }
};

// =============================================================================
// Unified Ad Interface
// =============================================================================

/**
 * Show ad based on platform (AdMob for native, placeholder/AdSense for web)
 * Returns a promise that resolves when ad is completed or fails
 */
export const showAd = async (callbacks?: AdCallbacks): Promise<{ success: boolean; rewarded: boolean }> => {
  if (isNativePlatform()) {
    // Use AdMob for native platforms
    let rewarded = false;

    const result = await showRewardedAd({
      ...callbacks,
      onRewardEarned: (reward) => {
        rewarded = true;
        callbacks?.onRewardEarned?.(reward);
      },
    });

    return { success: result, rewarded };
  } else {
    // For web, we simulate the ad experience
    // In production with AdSense, you would track impressions differently
    console.log("[AdService] Web platform - using simulated ad experience");

    callbacks?.onAdLoading?.();

    // Simulate ad loading delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    callbacks?.onAdLoaded?.();
    callbacks?.onAdShowing?.();

    // Note: For web, the "ad" is displayed inline in the component
    // The completion is handled by a timer in FreeModelDrawer

    return { success: true, rewarded: false };
  }
};

// =============================================================================
// Export Default Service Object
// =============================================================================

const adService = {
  // Platform detection
  isNativePlatform,
  isWebPlatform,

  // AdMob (native)
  initializeAdMob,
  prepareRewardedAd,
  showRewardedAd,

  // AdSense (web)
  isAdSenseLoaded,
  loadAdSenseScript,
  refreshAdSenseAd,

  // Unified
  showAd,

  // Config
  ADMOB_CONFIG,
  ADSENSE_CONFIG,
};

export default adService;
